import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  PencilIcon,
  TrashIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  TagIcon,
  CubeIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { supplierService, Supplier, ProductPriceHistory } from '../../services/supplierService'
import toast from 'react-hot-toast'

interface PurchaseItem {
  item_id: number
  product_id: number
  product_name: string
  sku: string
  quantity: number
  unit_price: number
  unit_price_base: number
  currency_code: string
  landed_cost: number
  suggested_retail_price: number
  margin_percent: number
}

export const SupplierDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'purchases' | 'price-lists'>('overview')
  const [selectedProduct, setSelectedProduct] = useState<PurchaseItem | null>(null)

  const { data: supplierData, isLoading } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => supplierService.getById(id!),
    enabled: !!id,
  })

  const { data: statsData } = useQuery({
    queryKey: ['supplier-stats', id],
    queryFn: () => supplierService.getStats(id!),
    enabled: !!id,
  })

  const { data: productsData } = useQuery({
    queryKey: ['supplier-products', id],
    queryFn: () => supplierService.getProducts(id!),
    enabled: !!id && activeTab === 'products',
  })

  const { data: priceListsData } = useQuery({
    queryKey: ['supplier-price-lists', id],
    queryFn: () => supplierService.getPriceLists(id!),
    enabled: !!id && activeTab === 'price-lists',
  })

  const { data: priceHistoryData } = useQuery({
    queryKey: ['supplier-product-price-history', id, selectedProduct?.product_id],
    queryFn: () => supplierService.getProductPriceHistory(id!, selectedProduct!.product_id),
    enabled: !!id && !!selectedProduct,
  })

  const deleteMutation = useMutation({
    mutationFn: () => supplierService.delete(id!),
    onSuccess: () => {
      toast.success('Supplier deleted successfully')
      navigate('/suppliers')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete supplier')
    },
  })

  const supplier: Supplier = supplierData?.data?.data
  const stats = statsData?.data?.data
  const products: PurchaseItem[] = productsData?.data?.data || []
  const priceLists = priceListsData?.data?.data || []
  const priceHistory: { product: any; supplier: any; history: ProductPriceHistory[] } = priceHistoryData?.data?.data

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!supplier) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Supplier not found</h2>
        <Link to="/suppliers" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to suppliers
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/suppliers" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{supplier.company_name}</h1>
            {supplier.supplier_code && (
              <p className="text-sm text-gray-500">{supplier.supplier_code}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/suppliers/${id}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <PencilIcon className="w-4 h-4" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCartIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-xl font-semibold text-gray-900">{stats?.total_orders || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Spent</p>
              <p className="text-xl font-semibold text-gray-900">
                KSh {(stats?.total_spent || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CubeIcon className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Products</p>
              <p className="text-xl font-semibold text-gray-900">{stats?.unique_products || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DocumentTextIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Price Lists</p>
              <p className="text-xl font-semibold text-gray-900">{supplier._count?.supplier_price_lists || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {(['overview', 'products', 'purchases', 'price-lists'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.replace('-', ' ')}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
              <div className="space-y-3">
                {supplier.contact_person && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                    <span>{supplier.contact_person}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                    <a href={`mailto:${supplier.email}`} className="text-primary-600 hover:underline">
                      {supplier.email}
                    </a>
                  </div>
                )}
                {supplier.phone && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <PhoneIcon className="w-5 h-5 text-gray-400" />
                    <a href={`tel:${supplier.phone}`} className="text-primary-600 hover:underline">
                      {supplier.phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPinIcon className="w-5 h-5 text-gray-400" />
                  <span>
                    {[supplier.address, supplier.city, supplier.state_province, supplier.country]
                      .filter(Boolean)
                      .join(', ') || 'No address provided'}
                  </span>
                </div>
                {supplier.tax_number && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <TagIcon className="w-5 h-5 text-gray-400" />
                    <span>Tax: {supplier.tax_number}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Currency & Terms */}
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Currency & Terms</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Default Currency</span>
                  <span className="font-medium">
                    {supplier.currencies?.symbol || supplier.default_currency_code} 
                    {' '}({supplier.default_currency_code})
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Payment Terms</span>
                  <span className="font-medium">
                    {supplier.payment_terms_days ? `Net ${supplier.payment_terms_days}` : 'Immediate'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Country</span>
                  <span className="font-medium">{supplier.country}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                    supplier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {supplier.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            {stats?.recent_orders?.length > 0 && (
              <div className="lg:col-span-2 bg-white rounded-xl shadow border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-sm font-medium text-gray-500">PO Number</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Date</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-500">Amount</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_orders.map((order: any) => (
                        <tr key={order.purchase_id} className="border-b last:border-0">
                          <td className="py-3">
                            <Link 
                              to={`/purchases/${order.purchase_id}`}
                              className="text-primary-600 hover:underline"
                            >
                              {order.purchase_number}
                            </Link>
                          </td>
                          <td className="py-3 text-gray-600">
                            {new Date(order.purchase_date).toLocaleDateString()}
                          </td>
                          <td className="py-3 text-right">
                            {order.currency_code} {Number(order.total_amount).toLocaleString()}
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                              order.status === 'received' ? 'bg-green-100 text-green-800' :
                              order.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Notes */}
            {supplier.notes && (
              <div className="lg:col-span-2 bg-white rounded-xl shadow border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
                <p className="text-gray-600 whitespace-pre-wrap">{supplier.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Last Price</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Landed Cost</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {products.map((product: any) => (
                      <tr key={product.product_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{product.product_name}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{product.sku}</td>
                        <td className="px-6 py-4 text-right">
                          {product.currency_code} {Number(product.last_price).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {product.last_landed_cost ? (
                            <span className="text-green-600">
                              KSh {Number(product.last_landed_cost).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => setSelectedProduct(product)}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            View History
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Price History Modal */}
            {selectedProduct && priceHistory && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                  <div className="p-6 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          Price History: {priceHistory.product.product_name}
                        </h3>
                        <p className="text-gray-500">SKU: {priceHistory.product.sku}</p>
                      </div>
                      <button
                        onClick={() => setSelectedProduct(null)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    {priceHistory.history.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No price history available</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Reference</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Landed Cost</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Suggested Retail</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {priceHistory.history.map((item, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">
                                  {new Date(item.date).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                    item.type === 'purchase' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                  }`}>
                                    {item.type === 'purchase' ? 'Purchase' : 'Price List'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{item.reference}</td>
                                <td className="px-4 py-3 text-right text-sm">
                                  {item.currency_code} {item.unit_price.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right text-sm">
                                  {item.landed_cost ? (
                                    <span className="text-green-600">
                                      KSh {item.landed_cost.toLocaleString()}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-sm">
                                  {item.suggested_retail_price ? (
                                    <span className="text-blue-600">
                                      KSh {item.suggested_retail_price.toLocaleString()}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'purchases' && (
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Purchase Orders</h2>
              <Link
                to={`/purchases/new?supplier=${id}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <PlusIcon className="w-4 h-4" />
                New Purchase
              </Link>
            </div>
            {supplier.purchases?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No purchase orders yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {supplier.purchases?.map((purchase: any) => (
                      <tr key={purchase.purchase_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <Link 
                            to={`/purchases/${purchase.purchase_id}`}
                            className="text-primary-600 hover:underline"
                          >
                            {purchase.purchase_number}
                          </Link>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {new Date(purchase.purchase_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {purchase._count?.purchase_items || 0} items
                        </td>
                        <td className="px-6 py-4 text-right">
                          {purchase.currency_code} {Number(purchase.total_amount).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            purchase.status === 'received' ? 'bg-green-100 text-green-800' :
                            purchase.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {purchase.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'price-lists' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Price Lists</h2>
              <Link
                to={`/suppliers/${id}/price-lists/new`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <PlusIcon className="w-4 h-4" />
                New Price List
              </Link>
            </div>
            {priceLists.length === 0 ? (
              <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
                <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No price lists yet</h3>
                <p className="text-gray-500 mb-4">Create a price list to track supplier pricing</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {priceLists.map((list: any) => (
                  <div key={list.list_id} className="bg-white rounded-xl shadow border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">{list.list_name || 'Price List'}</h3>
                      <span className="text-sm text-gray-500">
                        {new Date(list.effective_date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      {list.currencies?.symbol} {list.currency_code} • {list.supplier_price_list_items?.length || 0} items
                    </p>
                    {list.notes && (
                      <p className="text-sm text-gray-500 mb-4">{list.notes}</p>
                    )}
                    <div className="space-y-2">
                      {list.supplier_price_list_items?.slice(0, 5).map((item: any) => (
                        <div key={item.item_id} className="flex items-center justify-between text-sm">
                          <span className="truncate">{item.products?.product_name || 'Unknown Product'}</span>
                          <span className="font-medium">
                            {list.currencies?.symbol}{item.unit_price}
                          </span>
                        </div>
                      ))}
                      {list.supplier_price_list_items?.length > 5 && (
                        <p className="text-sm text-gray-500 text-center">
                          +{list.supplier_price_list_items.length - 5} more items
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
